import { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { forceCollide } from 'd3-force';
import { Button, Card, CardBody, Chip, Input } from 'cambrian-ds';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  RotateCcw,
  Filter,
  Search,
  X,
  ChevronRight,
  Info
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { cambrianColors, typography, spacing, effects } from '@/styles/design-tokens';
import type { KnowledgeGraph } from '@/types/knowledgeGraph';

interface KnowledgeGraphVisualizationProps {
  graph: KnowledgeGraph;
}

// Enhanced node colors using cambrian-ds design tokens
const NODE_COLORS: Record<string, string> = {
  repository: '#3B82F6',
  file: '#10B981',
  concept: '#F59E0B',
  function: '#8B5CF6',
  project: '#EF4444',
  tweet: '#06B6D4',
  query: '#10B981',
  historical_query: '#6B7280',
  recent_query: '#9CA3AF',
  completed_query: '#3B82F6',
  execution_plan: '#F59E0B',
  agent: '#8B5CF6',
  response: '#06B6D4',
  quality_assessment: '#10B981',
  feedback: '#EF4444',
};

const NODE_SIZES: Record<string, number> = {
  repository: 4,
  file: 2,
  concept: 3,
  function: 2,
  project: 4,
  tweet: 3,
  query: 7,  // User confirmed this size is good
  historical_query: 4,
  recent_query: 3,
  completed_query: 4,
  execution_plan: 4,
  agent: 3,  // Much smaller - agents were too large
  response: 2,
  quality_assessment: 3,
  feedback: 2,
};

const EDGE_COLORS: Record<string, string> = {
  contains: '#9CA3AF',
  implements: '#F59E0B',
  defines: '#8B5CF6',
  related_to: '#3B82F6',
  owned_by: '#EF4444',
  mentions: '#06B6D4',
  similar_to: '#10B981',
  has_plan: '#F59E0B',
  uses_agent: '#8B5CF6',
  generated: '#06B6D4',
  assessed: '#EF4444',
};

export function KnowledgeGraphVisualization({ graph }: KnowledgeGraphVisualizationProps) {
  const fgRef = useRef<any>();
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [highlightNodes, setHighlightNodes] = useState(new Set());
  const [highlightLinks, setHighlightLinks] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showControls, setShowControls] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [enabledNodeTypes, setEnabledNodeTypes] = useState<Set<string>>(new Set());
  const [enabledEdgeTypes, setEnabledEdgeTypes] = useState<Set<string>>(new Set());

  // Initialize all types as enabled
  useEffect(() => {
    const nodeTypes = new Set(graph.nodes.map(n => n.type));
    const edgeTypes = new Set(graph.edges.map(e => e.type));
    setEnabledNodeTypes(nodeTypes);
    setEnabledEdgeTypes(edgeTypes);
  }, [graph]);

  // Helper function to clean tweet text
  const cleanTweetText = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
      .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
      .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
      .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '')
      .replace(/[\u{2600}-\u{26FF}]/gu, '')
      .replace(/[\u{2700}-\u{27BF}]/gu, '')
      .replace(/https?:\/\/\S+/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .substring(0, 120);
  };

  // Transform data with filtering
  const graphData = useMemo(() => {
    const filteredNodes = graph.nodes
      .filter(node => enabledNodeTypes.has(node.type))
      .filter(node => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
          node.label.toLowerCase().includes(searchLower) ||
          node.type.toLowerCase().includes(searchLower) ||
          node.id.toLowerCase().includes(searchLower)
        );
      })
      .map(node => ({
        id: node.id,
        name: node.type === 'tweet' ? cleanTweetText(node.label) : node.label,
        type: node.type,
        properties: node.properties,
        color: NODE_COLORS[node.type] || '#9CA3AF',
        val: NODE_SIZES[node.type] || 14,
      }));

    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredLinks = graph.edges
      .filter(edge => enabledEdgeTypes.has(edge.type))
      .filter(edge => nodeIds.has(edge.source) && nodeIds.has(edge.target))
      .map(edge => ({
        source: edge.source,
        target: edge.target,
        type: edge.type,
        label: edge.label,
        color: EDGE_COLORS[edge.type] || '#6B7280',
      }));

    return {
      nodes: filteredNodes,
      links: filteredLinks,
    };
  }, [graph, enabledNodeTypes, enabledEdgeTypes, searchQuery]);

  // Get unique node and edge types
  const nodeTypes = useMemo(() => [...new Set(graph.nodes.map(n => n.type))].sort(), [graph]);
  const edgeTypes = useMemo(() => [...new Set(graph.edges.map(e => e.type))].sort(), [graph]);

  // Center graph on mount and data change
  useEffect(() => {
    if (fgRef.current && graphData.nodes.length > 0) {
      setTimeout(() => {
        fgRef.current?.zoomToFit(400, 50);
      }, 100);
    }
  }, [graphData]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    if (fgRef.current) {
      const currentZoom = fgRef.current.zoom();
      fgRef.current.zoom(currentZoom * 1.5, 400);
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (fgRef.current) {
      const currentZoom = fgRef.current.zoom();
      fgRef.current.zoom(currentZoom / 1.5, 400);
    }
  }, []);

  const handleFitView = useCallback(() => {
    if (fgRef.current) {
      fgRef.current.zoomToFit(400, 50);
    }
  }, []);

  const handleReset = useCallback(() => {
    setSelectedNode(null);
    setHighlightNodes(new Set());
    setHighlightLinks(new Set());
    setSearchQuery('');
    handleFitView();
  }, [handleFitView]);

  // Node interaction handlers
  const handleNodeClick = useCallback((node: any, event: any) => {
    // Double-click to unfreeze node position
    if (event && event.detail === 2) {
      node.fx = null;
      node.fy = null;
      return;
    }

    setSelectedNode(node);

    // Highlight connected nodes and links
    const connectedNodes = new Set([node.id]);
    const connectedLinks = new Set();

    graphData.links.forEach((link: any) => {
      if (link.source.id === node.id || link.target.id === node.id) {
        connectedLinks.add(link);
        connectedNodes.add(typeof link.source === 'object' ? link.source.id : link.source);
        connectedNodes.add(typeof link.target === 'object' ? link.target.id : link.target);
      }
    });

    setHighlightNodes(connectedNodes);
    setHighlightLinks(connectedLinks);
  }, [graphData]);

  const handleNodeHover = useCallback((node: any) => {
    if (!selectedNode && node) {
      const connectedNodes = new Set([node.id]);
      const connectedLinks = new Set();

      graphData.links.forEach((link: any) => {
        if (link.source.id === node.id || link.target.id === node.id) {
          connectedLinks.add(link);
          connectedNodes.add(typeof link.source === 'object' ? link.source.id : link.source);
          connectedNodes.add(typeof link.target === 'object' ? link.target.id : link.target);
        }
      });

      setHighlightNodes(connectedNodes);
      setHighlightLinks(connectedLinks);
    } else if (!selectedNode && !node) {
      setHighlightNodes(new Set());
      setHighlightLinks(new Set());
    }
  }, [selectedNode, graphData]);

  const toggleNodeType = (type: string) => {
    setEnabledNodeTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const toggleEdgeType = (type: string) => {
    setEnabledEdgeTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  return (
    <div className="relative w-full h-full flex gap-4">
      {/* Main Graph Area */}
      <div className="flex-1 relative rounded-lg overflow-hidden" style={{ backgroundColor: '#0F172A' }}>
        <ForceGraph2D
          ref={fgRef}
          graphData={graphData}
          nodeLabel={(node: any) => {
            const props = node.properties;
            let extraInfo = '';

            if (node.type === 'repository' && props.stars) {
              extraInfo = `<div class="text-xs text-gray-400">⭐ ${props.stars.toLocaleString()}</div>`;
            } else if (node.type === 'project') {
              extraInfo = `
                ${props.symbol ? `<div class="text-xs text-yellow-400 font-mono">$${props.symbol}</div>` : ''}
                ${props.description ? `<div class="text-xs text-gray-400 mt-1">${props.description.substring(0, 100)}...</div>` : ''}
              `;
            } else if (node.type === 'tweet') {
              const cleanText = cleanTweetText(props.text || node.name);
              extraInfo = `<div class="text-xs mt-2 text-gray-300 italic max-w-sm">"${cleanText}..."</div>`;
            }

            return `
              <div class="bg-gray-800 text-white p-3 rounded-lg shadow-lg max-w-xs">
                <div class="font-bold text-sm mb-1">${node.type}</div>
                <div class="text-xs mb-2">${node.name}</div>
                ${extraInfo}
              </div>
            `;
          }}
          nodeCanvasObject={(node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
            const isHighlighted = highlightNodes.has(node.id);
            const baseSize = node.val;
            const size = isHighlighted ? baseSize * 1.4 : baseSize;

            // Draw glow for highlighted nodes
            if (isHighlighted) {
              ctx.beginPath();
              ctx.arc(node.x!, node.y!, size + 4, 0, 2 * Math.PI);
              ctx.fillStyle = node.color + '40';
              ctx.fill();
            }

            // Draw node circle
            ctx.beginPath();
            ctx.arc(node.x!, node.y!, size, 0, 2 * Math.PI);
            ctx.fillStyle = node.color;
            ctx.fill();

            // Draw border for selected node
            if (node.id === selectedNode?.id) {
              ctx.strokeStyle = '#FFFFFF';
              ctx.lineWidth = 3;
              ctx.stroke();
            }

            // Draw label
            const label = node.name.length > 20 ? node.name.substring(0, 20) + '...' : node.name;
            const fontSize = Math.max(10, 14 / globalScale);
            ctx.font = `${fontSize}px Sans-Serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = isHighlighted ? '#FFFFFF' : '#D1D5DB';
            ctx.fillText(label, node.x!, node.y! + size + fontSize);
          }}
          linkCanvasObject={(link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
            const isHighlighted = highlightLinks.has(link);
            const width = isHighlighted ? 4 : 2;
            const alpha = isHighlighted ? 'FF' : '80';

            // Draw link
            ctx.beginPath();
            ctx.moveTo(link.source.x, link.source.y);
            ctx.lineTo(link.target.x, link.target.y);
            ctx.strokeStyle = (link.color || '#6B7280') + alpha;
            ctx.lineWidth = width;
            ctx.stroke();

            // Draw arrow
            if (isHighlighted) {
              const dx = link.target.x - link.source.x;
              const dy = link.target.y - link.source.y;
              const angle = Math.atan2(dy, dx);
              const arrowLength = 10;
              const arrowAngle = Math.PI / 6;

              ctx.save();
              ctx.translate(link.target.x, link.target.y);
              ctx.rotate(angle);
              ctx.beginPath();
              ctx.moveTo(-link.target.val - 5, 0);
              ctx.lineTo(-link.target.val - 5 - arrowLength, -arrowLength * Math.tan(arrowAngle));
              ctx.lineTo(-link.target.val - 5 - arrowLength, arrowLength * Math.tan(arrowAngle));
              ctx.closePath();
              ctx.fillStyle = link.color + alpha;
              ctx.fill();
              ctx.restore();
            }
          }}
          onNodeClick={handleNodeClick}
          onNodeHover={handleNodeHover}
          onNodeDragEnd={(node: any) => {
            // Fix node position when user stops dragging
            node.fx = node.x;
            node.fy = node.y;
          }}
          backgroundColor="#0F172A"
          nodeRelSize={2}
          linkHoverPrecision={10}
          linkWidth={(link: any) => (highlightLinks.has(link) ? 3 : 1)}
          linkDirectionalParticles={2}
          linkDirectionalParticleWidth={(link: any) => (highlightLinks.has(link) ? 4 : 0)}
          linkLabel={(link: any) => link.type}
          enableNodeDrag={true}
          enableZoomInteraction={true}
          enablePanInteraction={true}
          cooldownTicks={100}
          d3AlphaDecay={0.02}
          d3VelocityDecay={0.3}
          d3Force={(forces: any) => {
            // Disable link spring force - don't pull nodes together
            forces.link?.strength(0.1);  // Very weak attraction
            forces.link?.distance(200);  // Large distance between connected nodes

            // Strong repulsion to push nodes apart
            forces.charge?.strength(-800);

            // Minimal collision detection
            forces.collision = forceCollide((node: any) => {
              return (node.val || 3) + 2;
            });
          }}
        />

        {/* Zoom Controls Overlay */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          <Button
            onPress={handleZoomIn}
            size="sm"
            variant="secondary"
            isIconOnly
            className="bg-white/90 hover:bg-white"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            onPress={handleZoomOut}
            size="sm"
            variant="secondary"
            isIconOnly
            className="bg-white/90 hover:bg-white"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button
            onPress={handleFitView}
            size="sm"
            variant="secondary"
            isIconOnly
            className="bg-white/90 hover:bg-white"
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
          <Button
            onPress={handleReset}
            size="sm"
            variant="secondary"
            isIconOnly
            className="bg-white/90 hover:bg-white"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>

        {/* Stats Overlay */}
        <div className="absolute top-4 left-4">
          <div className={cn(
            "px-3 py-2 rounded-lg text-xs font-medium",
            "bg-white/90 backdrop-blur-sm",
            cambrianColors.text.secondary
          )}>
            {graphData.nodes.length} nodes • {graphData.links.length} edges
          </div>
        </div>
      </div>

      {/* Right Sidebar - Controls & Legend */}
      <div className="w-80 flex flex-col gap-4 overflow-y-auto max-h-full">
        {/* Search */}
        <Card>
          <CardBody className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Search className="w-4 h-4" />
              <span className={cn(typography.label, "font-semibold")}>Search Nodes</span>
            </div>
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, type, or ID..."
              size="sm"
              className="w-full"
            />
            {searchQuery && (
              <Button
                onPress={() => setSearchQuery('')}
                size="sm"
                variant="tertiary"
                className="mt-2 w-full"
              >
                Clear Search
              </Button>
            )}
          </CardBody>
        </Card>

        {/* Node Type Filters */}
        <Card>
          <CardBody className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                <span className={cn(typography.label, "font-semibold")}>Node Types</span>
              </div>
              <span className={cn(typography.small, cambrianColors.text.muted)}>
                {enabledNodeTypes.size}/{nodeTypes.length}
              </span>
            </div>
            <div className="space-y-2">
              {nodeTypes.map(type => (
                <label key={type} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={enabledNodeTypes.has(type)}
                    onChange={() => toggleNodeType(type)}
                    className="rounded"
                  />
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: NODE_COLORS[type] || '#9CA3AF' }}
                  />
                  <span className={cn(typography.small, "group-hover:text-gray-900")}>
                    {type}
                  </span>
                  <span className={cn(typography.small, cambrianColors.text.muted, "ml-auto")}>
                    {graph.nodes.filter(n => n.type === type).length}
                  </span>
                </label>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Edge Type Filters */}
        <Card>
          <CardBody className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4" />
                <span className={cn(typography.label, "font-semibold")}>Edge Types</span>
              </div>
              <span className={cn(typography.small, cambrianColors.text.muted)}>
                {enabledEdgeTypes.size}/{edgeTypes.length}
              </span>
            </div>
            <div className="space-y-2">
              {edgeTypes.map(type => (
                <label key={type} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={enabledEdgeTypes.has(type)}
                    onChange={() => toggleEdgeType(type)}
                    className="rounded"
                  />
                  <div
                    className="w-8 h-0.5"
                    style={{ backgroundColor: EDGE_COLORS[type] || '#6B7280' }}
                  />
                  <span className={cn(typography.small, "group-hover:text-gray-900")}>
                    {type}
                  </span>
                  <span className={cn(typography.small, cambrianColors.text.muted, "ml-auto")}>
                    {graph.edges.filter(e => e.type === type).length}
                  </span>
                </label>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* Selected Node Details */}
        {selectedNode && (
          <Card>
            <CardBody className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  <span className={cn(typography.label, "font-semibold")}>Selected Node</span>
                </div>
                <Button
                  onPress={() => {
                    setSelectedNode(null);
                    setHighlightNodes(new Set());
                    setHighlightLinks(new Set());
                  }}
                  size="sm"
                  variant="tertiary"
                  isIconOnly
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3">
                {/* Node Type Badge */}
                <div>
                  <Chip
                    color="primary"
                    size="sm"
                    style={{ backgroundColor: selectedNode.color }}
                  >
                    {selectedNode.type}
                  </Chip>
                </div>

                {/* Properties Table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-b bg-gray-50">
                        <td className="py-2 px-3 font-medium text-gray-600">Name</td>
                        <td className="py-2 px-3 text-gray-900 break-words">{selectedNode.name}</td>
                      </tr>
                      <tr className="border-b bg-white">
                        <td className="py-2 px-3 font-medium text-gray-600">ID</td>
                        <td className="py-2 px-3 text-gray-900 font-mono text-xs break-all">{selectedNode.id}</td>
                      </tr>
                      {Object.entries(selectedNode.properties || {}).map(([key, value]: [string, any]) => (
                        <tr key={key} className="border-b bg-white hover:bg-gray-50">
                          <td className="py-2 px-3 font-medium text-gray-600 align-top">{key}</td>
                          <td className="py-2 px-3 text-gray-900 break-words">
                            {typeof value === 'object'
                              ? JSON.stringify(value, null, 2).substring(0, 100) + (JSON.stringify(value).length > 100 ? '...' : '')
                              : String(value).substring(0, 100) + (String(value).length > 100 ? '...' : '')
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Connected Nodes */}
                <div>
                  <h4 className={cn(typography.small, "font-semibold mb-2")}>
                    Connected To ({highlightNodes.size - 1} nodes)
                  </h4>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {graphData.nodes
                      .filter((n: any) => highlightNodes.has(n.id) && n.id !== selectedNode.id)
                      .map((n: any) => {
                        const link = graphData.links.find((l: any) =>
                          (l.source.id === selectedNode.id && l.target.id === n.id) ||
                          (l.target.id === selectedNode.id && l.source.id === n.id)
                        );
                        return (
                          <div
                            key={n.id}
                            className="flex items-center gap-2 p-2 rounded bg-gray-50 hover:bg-gray-100 cursor-pointer text-xs"
                            onClick={() => handleNodeClick(n)}
                          >
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: n.color }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{n.name}</div>
                              <div className="text-gray-500 text-xs">
                                {n.type}
                                {link && <span className="ml-1 text-gray-400">• {link.type}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
}
